import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import SEO from "../components/SEO";

// Proper nouns and standards (kept untranslated for every locale)
const HERO_BG = "https://images.unsplash.com/photo-1592288801240-7b18bb06e94f?w=2400&q=80";
const MID_BREAK = "https://images.pexels.com/photos/28410257/pexels-photo-28410257.jpeg?w=2400";
const HERO_BADGES = ["BMV Global", "B3 · CPR Verde", "Banco Central do Brasil", "ISO 14064", "TNFD", "REDD+"];
const STANDARDS = [
  "Green Bond Principles (GBP)",
  "Green Loan Principles (GLP)",
  "Sustainable Bonds for Nature",
  "Climate Bonds Initiative Taxonomy",
  "EU Sustainable Finance Taxonomy",
  "UN Sustainable Development Goals (SDGs)",
  "Anbima ESG Bonds Guide",
  "Febraban Sustainable Debt Guide",
  "Brazilian Sustainable Taxonomy",
  "ICROA",
  "ISO 14064",
  "TNFD",
];

const DOCS = [
  { title: "SPO — Sustainability Credit Units · BMV Group", date: "2025-07-25", size: "1.2 MB", lang: "EN", primary_for: ["en", "es", "fr", "ar"],
    href: "https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/5e8xcf7c_SPO%20Sustainability%20Credit%20Units%20-%20BMV%20Group%2020250725.pdf" },
  { title: "BMV — Precificação UCS de Crédito de Floresta (EY · PT)", date: "2022-01-22", size: "3.0 MB", lang: "PT", primary_for: ["pt"],
    href: "https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/5s2galk4_200122%20BMV%20-%20Precifica%C3%A7%C3%A3o%20UCS%20de%20Cr%C3%A9dito%20de%20Floresta%20EY%20%28PT%29%20vf.pdf" },
  { title: "Standards for Results-Based REDD+ Finance", date: "—", size: "2.0 MB", lang: "EN", primary_for: [],
    href: "https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/eaamf36x_Standards_for_Results_Based_REDD_Finance.pdf" },
  { title: "BMV Global — Presentation", date: "—", size: "3.5 MB", lang: "EN", primary_for: [],
    href: "https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/h22lwjmz_new%20Presentation.pdf" },
  { title: "BMV Global — Conformidade e Governança", date: "2026", size: "948 KB", lang: "PT", primary_for: ["pt"],
    href: "https://customer-assets.emergentagent.com/job_credit-marketplace-11/artifacts/pa0eh8vl_2026-BMV%20Global%20CONFORMIDADE%20E%20GOVERNANC%CC%A7A%20docx.pdf" },
];

export default function Methodology() {
  const { t, i18n } = useTranslation();
  const pillars = [
    { code: "01", title: t("meth.pillar1_t"), body: t("meth.pillar1_b", { returnObjects: true }) },
    { code: "02", title: t("meth.pillar2_t"), body: t("meth.pillar2_b", { returnObjects: true }) },
    { code: "03", title: t("meth.pillar3_t"), body: t("meth.pillar3_b", { returnObjects: true }) },
  ];
  const lifecycle = t("meth.lc", { returnObjects: true }) || [];
  const regulators = t("meth.reg", { returnObjects: true }) || [];
  const quotes = t("meth.quotes", { returnObjects: true }) || [];
  const adcBullets = t("meth.adc_b", { returnObjects: true }) || [];
  const govBullets = t("meth.gov_b", { returnObjects: true }) || [];

  return (
    <Layout>
      <SEO
        title="Methodology — BMV Global UCS Standard"
        description="How UCS carbon credits are measured, audited and verified. BMV Global methodology with three pillars: economic, territorial and environmental. Based on ISO 14064, IPCC and B3 registration."
        path="/methodology"
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="rainforest canopy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/75 to-bg/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-bg" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-20">
          <div className="label-tag mb-4" data-testid="methodology-tag">{t("meth.tag")}</div>
          <h1 className="font-serif text-5xl lg:text-6xl tracking-tight max-w-4xl">{t("meth.h1")}</h1>
          <p className="text-ink2 mt-5 max-w-3xl text-lg leading-relaxed">
            {t("meth.intro_a")} {t("meth.intro_b")} <span className="mono-num text-ink">1 tCO₂e</span> {t("meth.intro_c")}
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {HERO_BADGES.map((b) => (
              <span key={b} className="px-3 py-1.5 bg-surface/80 backdrop-blur border border-line font-mono text-[11px] tracking-wider uppercase">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-10">
        <div className="label-tag mb-6">{t("meth.pillars_label")}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
          {pillars.map((p) => (
            <article key={p.code} className="bg-surface p-7" data-testid={`pillar-${p.code}`}>
              <div className="mono-num text-xs text-ink2 tracking-widest">PILLAR · {p.code}</div>
              <h3 className="font-serif text-3xl mt-2">{p.title}</h3>
              <ul className="mt-5 space-y-3 text-sm text-ink2 leading-relaxed">
                {(Array.isArray(p.body) ? p.body : []).map((line) => (
                  <li key={line} className="flex gap-3">
                    <span className="text-leaf mt-1.5 w-1.5 h-1.5 bg-leaf shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Lifecycle */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="label-tag mb-3">{t("meth.lc_label")}</div>
            <h2 className="font-serif text-4xl tracking-tight">{t("meth.lc_h")}</h2>
            <p className="text-ink2 mt-4 text-sm leading-relaxed">{t("meth.lc_intro")}</p>
          </div>
          <ol className="lg:col-span-8 border border-line">
            {lifecycle.map((s, i) => {
              const code = String(i + 1).padStart(2, "0");
              return (
                <li key={code} className={`flex gap-6 p-6 bg-surface ${i < lifecycle.length - 1 ? "border-b border-line" : ""}`} data-testid={`lifecycle-${code}`}>
                  <div className="mono-num text-ink2 text-2xl shrink-0 w-12">{code}</div>
                  <div>
                    <div className="font-serif text-xl">{s[0]}</div>
                    <div className="text-sm text-ink2 mt-1.5 leading-relaxed">{s[1]}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Mid-section nature break */}
      <section className="relative h-72 overflow-hidden my-8" data-testid="meth-image-break">
        <img src={MID_BREAK} alt="rainforest river" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-bg via-transparent to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 h-full flex items-end pb-10">
          <div className="text-white max-w-xl">
            <div className="text-[11px] font-mono tracking-[0.2em] uppercase opacity-90">UCS · 1 tCO₂e</div>
            <div className="font-serif text-3xl mt-2 leading-tight">Every unit traceable to a place, a producer, an audit.</div>
          </div>
        </div>
      </section>

      {/* Regulators */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 border-t border-line">
        <div className="label-tag mb-6">{t("meth.reg_label")}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line">
          {regulators.map((r) => (
            <div key={r[0]} className="bg-surface p-6">
              <div className="font-serif text-lg">{r[0]}</div>
              <div className="text-sm text-ink2 mt-1.5">{r[1]}</div>
            </div>
          ))}
        </div>
        <div className="mt-10">
          <div className="label-tag mb-4">{t("meth.std_label")}</div>
          <div className="flex flex-wrap gap-2">
            {STANDARDS.map((s) => (
              <span key={s} className="px-3 py-1.5 border border-line font-mono text-[11px] tracking-wider uppercase text-ink2 hover:text-ink hover:border-ink transition-colors">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Anti-double-counting + Governance */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-2 gap-px bg-line border border-line">
        <div className="bg-surface p-8">
          <div className="label-tag">{t("meth.adc_label")}</div>
          <h3 className="font-serif text-2xl mt-2">{t("meth.adc_h")}</h3>
          <ul className="mt-5 space-y-3 text-sm text-ink2 leading-relaxed">
            {adcBullets.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
        <div className="bg-surface p-8">
          <div className="label-tag">{t("meth.gov_label")}</div>
          <h3 className="font-serif text-2xl mt-2">{t("meth.gov_h")}</h3>
          <ul className="mt-5 space-y-3 text-sm text-ink2 leading-relaxed">
            {govBullets.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
      </section>

      {/* Quotes */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="label-tag mb-6">{t("meth.q_label")}</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line border border-line">
          {quotes.map((q) => (
            <figure key={q[1]} className="bg-surface p-7">
              <blockquote className="font-serif text-lg leading-snug text-ink">“{q[0]}”</blockquote>
              <figcaption className="mt-5 text-xs font-mono tracking-wider uppercase text-ink2">— {q[1]}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Source documents */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 border-t border-line">
        <div className="label-tag mb-4">{t("meth.src_label")}</div>
        <h2 className="font-serif text-3xl tracking-tight mb-8">{t("meth.src_h")}</h2>
        <div className="border border-line bg-surface">
          {[...DOCS]
            .sort((a, b) => {
              const lang = (i18n.language || "en").slice(0, 2);
              const aPrim = a.primary_for.includes(lang) ? 0 : 1;
              const bPrim = b.primary_for.includes(lang) ? 0 : 1;
              return aPrim - bPrim;
            })
            .map((d, i, arr) => {
              const lang = (i18n.language || "en").slice(0, 2);
              const isPrimary = d.primary_for.includes(lang);
              return (
                <a
                  key={d.title}
                  href={d.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`source-doc-${i}`}
                  className={`flex items-center justify-between gap-6 p-5 hover:bg-muted/30 transition-colors ${i < arr.length - 1 ? "border-b border-line" : ""}`}
                >
                  <div className="flex items-center gap-5 min-w-0">
                    <div className="mono-num text-ink2 text-xs w-8 shrink-0">{d.lang}</div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="font-serif text-lg truncate">{d.title}</div>
                        {isPrimary && (
                          <span className="px-2 py-0.5 bg-leaf text-white text-[10px] font-mono uppercase tracking-wider shrink-0" data-testid={`primary-badge-${i}`}>
                            {t("meth.src_primary")}
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-mono text-ink2 mt-1">{d.date} · {d.size}</div>
                    </div>
                  </div>
                  <div className="text-xs font-mono tracking-wider uppercase text-ink2 shrink-0">{t("meth.src_open")}</div>
                </a>
              );
            })}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="border border-ink p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 bg-surface">
          <div>
            <div className="label-tag mb-3">{t("meth.cta_label")}</div>
            <h2 className="font-serif text-4xl tracking-tight max-w-2xl">{t("meth.cta_h")}</h2>
          </div>
          <div className="flex gap-3">
            <Link to="/marketplace" className="btn-primary !px-6 !py-3 !text-xs whitespace-nowrap" data-testid="methodology-cta-marketplace">
              {t("meth.cta_market")}
            </Link>
            <Link to="/register" className="btn-outline !px-6 !py-3 !text-xs whitespace-nowrap" data-testid="methodology-cta-register">
              {t("meth.cta_register")}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
